/** 提现环节超级会员卡片接入
 * 设计稿地址：https://www.figma.com/design/AdVYyTUKFJh8MzcmYqbHIv/%E8%87%BB%E6%9C%89%E9%92%B1%E5%A4%A7%E4%BC%9A%E5%91%98?node-id=1765-6650&t=YqMZfJKkTOdq2Xsx-0
*/
import Vue from 'vue';
import {
  Component,
  Prop,
  Watch,
} from 'vue-property-decorator';

import type { SuperCardSummaryBackType } from '@/api/rights/types';

import {
  PackageBuyStatusEnum,
  RightPackageTypeEnum,
  SuperMemberSourceEnum,
  GalileoExpSceneEnum,
} from '@/api/rights/types';

import { openInnerPage } from '@/api/native/utils';
import { MembersRightsPrefix } from '@/constant';
import { queryBuyStatus } from '@/api/rights/order/order';

import { namespace } from 'vuex-class';
import {
  LoanApplyMarketReqType,
  ApplyChannelData,
  CalcPlanListData,
  ProductCapitalItem,
} from '@/api/apply-product/types';
import { ForceReadService, ForceReadServiceReturnType } from '@/widgets/right-contract';
import { getGalileoExpResult } from '@/api/rights';
import URLUtils from '@/shared/utils/URLUtils';
import CheckBox from '@/components/check-box';
import ProductCommodity from '@/shared/commodity';
import style from './index.module.scss';
import SuperMemberCardView from './components/super-member-card-view/index.vue';
import SuperMemberPlusCardView from './components/super-member-plus-card-view/index.vue';
import { showAutarkyRetainRightsPopup, hideAutarkyRetainRightsPopup, destroyAutarkyRetainRightsPopup } from './components/retain-pop/retainPopService';

const ApplyProductStore = namespace('applyProduct');
const LoanRightStore = namespace('loanRight');

export type SuperMemberCardInstance = {
  check: boolean,
  checkBuy: () => Promise<boolean>,
  getRightSumbitParams: () => LoanApplyMarketReqType | undefined
  destroyContract: () => void
}

@Component({
  name: 'superMemberCard',
})
export default class superMemberCard extends Vue {
  @Prop(Number) readonly productId!: number;

  @Prop(String) readonly capitalCode!: string;

  @ApplyProductStore.Getter needBuySuperMember!: boolean;

  @ApplyProductStore.Getter needBuyRight!: boolean;

  @ApplyProductStore.State planListData!: CalcPlanListData | null;

  @ApplyProductStore.State selectRightInCapital!: ProductCapitalItem | null;

  @ApplyProductStore.State channel!: ApplyChannelData | null;

  @ApplyProductStore.State channelExtendObj!: Dictionary;

  @LoanRightStore.State showSuperMemberCard!: boolean;

  @LoanRightStore.State isSuperMemberCheck!: boolean | null;

  @LoanRightStore.State superMemberInfo!: SuperCardSummaryBackType | null;

  @LoanRightStore.Action('$updateState') updateLoanRightState!: (data: Dictionary) => void;

  /** 是否勾选同意协议 */
  public check = false;

  /** 是否已经购买标记判断
   * 用于用户在卡片页主动进入会员卡购买流程且购买，回退页面时展示购买后样式 */
  private hasBuy = false;

  /** 包一层 防止removeEventListener不生效 */
  private visibilityBind = this.visibilityHandler.bind(this);

  private superMemberContractInstance: ForceReadServiceReturnType | null = null;

  // 新增：存储当前的packageType，用于判断是否需要重新初始化
  private currentPackageType: RightPackageTypeEnum | null = null;

  private get superMemberShow() {
    if (this.superMemberInfo) {
      return !!Object.keys(this.superMemberInfo).length;
    }
    return false;
  }

  /** 统一埋点参数上报 */
  private get tqParams() {
    const {
      packageId, supplierCode, sellPrice, packageType,
    } = this.superMemberInfo || {};

    // 资金类型
    const { rateType } = this.selectRightInCapital || {};
    // 降价人群标签 用户标签
    const { userTag, userLoanRate24ZyUsr } = this.channel || {};
    // 综合利率
    const { rateValue } = this.planListData || {};

    return {
      productId: this.productId,
      capitalCode: String(this.productId) === ProductCommodity.AUTARKY_B_COMMODITY
        ? this.capitalCode : undefined,
      packageId,
      supplierCode,
      sellPrice,
      packageType,
      from: SuperMemberSourceEnum.FINANCE_CREDIT_LOAN_APPLY_CJHY,
      rateType: rateType || '',
      rateValue,
      userTag,
      userLoanRate24ZyUsr,
      newSuperMemberUserCheckTag: this.channelExtendObj?.newSuperMemberUserCheckTag,
    };
  }

  /** 根据超会返回的接口内容来选择是否要曝光超级会员卡片 */
  @Watch('superMemberInfo')
  private doExposeSuperMemberInfo() {
    if (this.superMemberInfo?.packageId) {
      this.$ubtModuleExposeNew('finance_loan_packageEntry', {
        ...this.tqParams,
        /** 联营超级会员样式实验埋点 */
        style: this.styleType,
      });
    }
  }

  /** 修复部分情况下，isSuperMemberCheck变更但是showSuperMemberCard在此之前变更
   * 导致没处理到状态响应
   * */
  @Watch('isSuperMemberCheck')
  private onSuperMemberCheckChange(newVal: boolean) {
    this.check = newVal || false;
  }

  /** 初始化数据 */
  @Watch('showSuperMemberCard', { immediate: true })
  private initData() {
    if (this.showSuperMemberCard) {
      this.check = this.isSuperMemberCheck || false;

      const { packageType = RightPackageTypeEnum.SUPER_MEMBER_CARD } = this.superMemberInfo || {};

      // 如果已初始化且packageType相同，则不需要重新初始化
      if (!this.superMemberContractInstance || this.currentPackageType !== packageType) {
        // 销毁旧的实例
        if (this.superMemberContractInstance) {
          this.superMemberContractInstance.destroyInstance();
        }

        // 初始化新的实例
        this.superMemberContractInstance = ForceReadService({
          packageType,
          forceReadFromScene: SuperMemberSourceEnum.FINANCE_CREDIT_LOAN_APPLY_CJHY,
        });
        this.currentPackageType = packageType;
      }
    } else {
      this.check = false;
    }
  }


  /** 导出会员重点下单信息，供后端调用 */
  public getRightSumbitParams(): LoanApplyMarketReqType | undefined {
    if (this.check && this.superMemberShow) {
      const { packageId, packageType } = this.superMemberInfo || {};
      return {
        packageId,
        packageType,
        packageFrom: SuperMemberSourceEnum.FINANCE_CREDIT_LOAN_APPLY_CJHY,
      };
    }
    return undefined;
  }

  /**
   * 会员卡片内部check方法，供外部调用
   * 1. 如果展示了会员卡片，check是否勾选
   *    未勾选
   *    接入实验 命中实验组弹出挽留弹窗，命中对照组走提现流程
   *    已勾选do nothing
   * 2. 如果未展示会员卡片，return
  */
  public async checkBuy(): Promise<boolean> {
    if (this.superMemberInfo && this.superMemberInfo.contractInfo && !this.hasBuy) {
      if (this.check) {
        return Promise.resolve(true);
      }

      if (this.superMemberInfo?.packageType === RightPackageTypeEnum.SUPER_MEMBER_CARD) {
        // 获取实验结果
        const { isHitExp } = await getGalileoExpResult({
          expScene: GalileoExpSceneEnum.SUPER_CARD_SUBMIT_RETAIN_POP_AB,
          productId: String(this.productId),
        });

        // 命中实验组弹出挽留弹窗
        if (isHitExp) {
        // 展示挽留弹窗
          const { success } = await showAutarkyRetainRightsPopup({
            superMemberInfo: this.superMemberInfo || {},
            tqParams: this.tqParams,
            contractPreview: this.showRetainPopContract.bind(this),
          });

          // 销毁挽留弹窗
          destroyAutarkyRetainRightsPopup();

          // 如果用户同意协议，则勾选协议
          if (success) {
            this.check = true;
          }
        }
      }

      return Promise.resolve(true);
    }
    return Promise.resolve(true);
  }

  /** 打开协议弹窗 */
  private async openContractPop() {
    const { contractInfo } = this.superMemberInfo || {};
    if (contractInfo) {
      const handleRes = await this.superMemberContractInstance?.handleForceRead({
        contractScene: this.superMemberInfo?.contractInfo?.scene || '',
        preview: true,
        tqParams: this.tqParams,
      }) || false;
      // 只有勾选时再改变勾选状态
      if (handleRes) {
        this.check = true;
      }
    }
  }

  /** 权益卡片上协议点击按钮 */
  private openContract() {
    // 点击查看合同埋点
    this.$ubtPageButtonNew('finance_loan_packageEntry_agreement', {
      ...this.tqParams,
    });
    this.openContractPop();
  }

  private async showRetainPopContract() {
    await this.superMemberContractInstance?.handleForceRead({
      contractScene: this.superMemberInfo?.contractInfo?.scene || '',
      preview: true,
      tqParams: this.tqParams,
    });
  }

  /** 勾选事件触发 */
  private async checkChange() {
    if (this.check) {
      this.$ubtPageButtonNew('finance_loan_packageEntry_agreementCheck', {
        ...this.tqParams,
        /** 联营超级会员样式实验埋点 */
        style: this.styleType,
      });

      // 修改：handleForceRead内部使用的是初始化时的packageType，不需要在调用时传入
      if (this.superMemberContractInstance) {
        this.check = await this.superMemberContractInstance.handleForceRead({
          contractScene: this.superMemberInfo?.contractInfo?.scene || '',
          tqParams: this.tqParams,
        }) || !this.check;
      }
    }

    // 将当前的勾选状态更新至store
    this.updateLoanRightState({
      isSuperMemberCheck: this.check,
    });
  }

  /** 获取当前购买状态，展示对应的购买页 */
  private async visibilityHandler() {
    let bought: boolean;
    try {
      const { packageType = RightPackageTypeEnum.SUPER_MEMBER_CARD } = this.superMemberInfo || {};
      const buyStatusRes = await queryBuyStatus({ packageType });
      bought = buyStatusRes.packageBuyStatus === PackageBuyStatusEnum.BOUGHT;
    } catch {
      bought = false;
    }

    // 权益有数据 且会员的购买状态为已购买
    this.hasBuy = this.superMemberShow && bought;
    // 计算完毕后移除监听
    if (this.hasBuy) {
      document.removeEventListener('visibilitychange', this.visibilityBind);
    }
  }

  /** 去权益详情页 */
  private goDetail() {
    const rightsUrl = URLUtils.formatUrl(
      `${MembersRightsPrefix}super-member.html#/super-member`,
      {
        from: SuperMemberSourceEnum.FINANCE_CREDIT_LOAN_APPLY_CJHY_DETAIL,
        packageId: this.superMemberInfo?.packageId,
        packageType: this.superMemberInfo?.packageType,
        hiddenNavigationBar: 1,
      },
    );
    openInnerPage(rightsUrl);

    // 开启监听，返回时刷新数据
    document.addEventListener('visibilitychange', this.visibilityBind);
  }


  public destroyContract(): void {
    if (this.superMemberContractInstance) {
      this.superMemberContractInstance.destroyInstance();
    }
    destroyAutarkyRetainRightsPopup();
  }

  private get styleType() {
    const { extraInfo } = this.superMemberInfo || {};
    return extraInfo && extraInfo.style;
  }

  // 会员卡片渲染
  private render() {
    if (!this.superMemberShow) {
      return null;
    }

    const isPlusCard = this.superMemberInfo?.packageType
      === RightPackageTypeEnum.SUPER_MEMBER_PLUS_CARD;
    const { contractInfo = { scene: '', name: '' } } = this.superMemberInfo || {};

    return (
      <div class={style.superMemberCard}>
        {!this.hasBuy && (
          isPlusCard ? (
            <SuperMemberPlusCardView
              superMemberInfo={this.superMemberInfo}
              onGoDetail={this.goDetail.bind(this)}
            >
              <div class={style.contractWrapper}>
                <div class={style.checkBoxClickWrapper}>
                  <CheckBox v-model={this.check}
                    onChange={this.checkChange.bind(this)}
                    class={style.checkBoxWrapper} />
                </div>
                <div class={style.contractTextWrapper}>
                  <span class={style.contractText}>我已阅读并同意</span>
                  <span class={style.contractName} onClick={this.openContract.bind(this)}>
                    《会员综合协议》
                  </span>
                  <span class={style.contractText}>，权益由臻有钱及供应商提供，与最终放款机构无关</span>
                </div>
                </div>
              </SuperMemberPlusCardView>
          ) : (
            <SuperMemberCardView
              superMemberInfo={this.superMemberInfo}
              onGoDetail={this.goDetail.bind(this)}
            >
              <div class={style.contractWrapper}>
                <div class={style.checkBoxClickWrapper}>
                   <CheckBox v-model={this.check}
                    onChange={this.checkChange.bind(this)}
                    class={style.checkBoxWrapper} />
                </div>
                <div>
                  <span class={style.contractText}>我已阅读并同意</span>
                  <span class={style.contractName} onClick={this.openContract.bind(this)}>
                    {contractInfo && `《${contractInfo.name}》`}
                  </span>
                </div>
              </div>
            </SuperMemberCardView>
          )
        )}
        {/* {this.hasBuy && (
          <div class={style.superMemberCardBought}>
            超级会员生效中，可在我的-权益中心查看
          </div>
        )} */}
      </div>
    );
  }
}
